import React from 'react';
import { devWarning } from './devWarning';
import { getComponentName } from './getComponentName';
import { OmitStrict } from './types/OmitStrict';

export interface HasControlledProps {
  isPropControlled: (propName: PropName<this>) => boolean;
}

export type OriginalComponentProps<T extends HasControlledProps> = OmitStrict<
  T,
  keyof HasControlledProps
>;

type PropName<T extends HasControlledProps> = keyof OriginalComponentProps<T> & string;

/**
 * Higher Order Component for warning when components switch from using internal state (as
 * uncontrolled components) to external state (as controlled components) without remounting.
 * @param Component the component to be wrapped
 * @param controllableProps props which can either be externally managed or internally managed.
 */
export function withControlledProps<T extends HasControlledProps, U>(
  controllableProps = [] as PropName<T>[],
) {
  interface HigherOrderProps {
    componentRef: React.Ref<U>;
    children?: React.ReactNode;
  }

  type ComposedComponentProps = OriginalComponentProps<T> & HigherOrderProps;

  return (Component: React.ComponentType<T>) => {
    const componentName = getComponentName(Component);

    class ControlledComponentsWarning extends React.Component<ComposedComponentProps> {
      public static readonly displayName = `ControlledComponentWrapper[${componentName}]`;

      private readonly initiallyControlled: { [key: string]: boolean };

      public constructor(props: ComposedComponentProps) {
        super(props);
        this.initiallyControlled = controllableProps.reduce(
          (acc, propName) => ({ ...acc, [propName]: this.isPropControlled(propName) }),
          {},
        );
      }

      public componentDidUpdate() {
        if (process.env.NODE_ENV === 'production') {
          return;
        }

        controllableProps.forEach((propName) => {
          const wasControlled = this.initiallyControlled[propName];

          devWarning(
            wasControlled === this.isPropControlled(propName),
            `"${propName}" prop on component ${componentName} was initialized as ${
              wasControlled ? 'controlled' : 'uncontrolled'
            }, but is now ${
              wasControlled ? 'uncontrolled' : 'controlled'
            }, and the component was not remounted. This may lead to unintended behavior. See: https://reactjs.org/docs/forms.html#controlled-components`,
          );
        });
      }

      private isPropControlled = (propName: PropName<T>) =>
        (this.props as any)[propName] !== undefined; // eslint-disable-line @typescript-eslint/no-explicit-any

      public render() {
        const { componentRef, ...rest } = this.props;

        const props = {
          ref: componentRef,
          isPropControlled: this.isPropControlled,
          ...rest,
        } as unknown as T;

        return <Component {...props} />;
      }
    }

    return React.forwardRef<U, OriginalComponentProps<T>>((props, ref) => (
      <ControlledComponentsWarning {...(props as ComposedComponentProps)} componentRef={ref} />
    ));
  };
}
